import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import UserAvatar from '../components/UserAvatar';
import { useAuth } from '../contexts/AuthContext';
import { PLANBASE_CONFIG } from '../config';
import { useProjectInteractions } from '../hooks/useProjectInteractions';
import { useProjectViews } from '../hooks/useProjectViews';
import { useProjects } from '../hooks/useProjects';
import { useHideBottomNav } from '../hooks/useHideBottomNav';

import { ArrowLeftIcon, ArrowRightIcon, BookmarkIcon, BuildingOffice2Icon, CalendarIcon, ChevronLeftIcon, EyeIcon, HeartIcon, MapPinIcon, PhotoIcon, PrinterIcon, ShareIcon, SwatchIcon, TagIcon, UserIcon, XMarkIcon } from '@heroicons/react/24/outline';

// Lightbox Component
function Lightbox({ 
  images, 
  currentIndex, 
  onClose, 
  onPrev, 
  onNext,
  tags 
}: { 
  images: any[]; 
  currentIndex: number; 
  onClose: () => void; 
  onPrev: () => void; 
  onNext: () => void;
  tags?: string[];
}) {
  const currentImage = images[currentIndex];
  const imageUrl = typeof currentImage === 'string' ? currentImage : currentImage?.url;
  const imageTitle = typeof currentImage === 'object' ? currentImage?.title : null;
  const imageDescription = typeof currentImage === 'object' ? currentImage?.description : null;

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/95 flex flex-col" onClick={onClose}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 md:p-4 text-white shrink-0">
        <span className="text-sm">{currentIndex + 1} / {images.length}</span>
        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
          <XMarkIcon className="h-5 w-5 md:h-6 md:w-6" />
        </button>
      </div>

      {/* Main Image Area */}
      <div className="flex-1 flex items-center justify-center relative px-2 md:px-4 min-h-0" onClick={(e) => e.stopPropagation()}>
        {/* Prev Button - Hidden on mobile, use swipe instead */}
        {images.length > 1 && (
          <button 
            onClick={onPrev}
            className="hidden md:flex absolute left-2 md:left-4 p-2 md:p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
          >
            <ArrowLeftIcon className="h-5 w-5 md:h-6 md:w-6 text-white" />
          </button>
        )}

        {/* Image */}
        <img
          src={imageUrl}
          alt={imageTitle || `Image ${currentIndex + 1}`}
          className="max-h-[60vh] md:max-h-[70vh] max-w-full object-contain"
          onError={(e) => {
            e.currentTarget.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop';
          }}
        />

        {/* Next Button - Hidden on mobile */}
        {images.length > 1 && (
          <button 
            onClick={onNext}
            className="hidden md:flex absolute right-2 md:right-4 p-2 md:p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
          >
            <ArrowRightIcon className="h-5 w-5 md:h-6 md:w-6 text-white" />
          </button>
        )}
      </div>

      {/* Mobile Navigation Buttons */}
      {images.length > 1 && (
        <div className="flex md:hidden justify-center gap-4 py-2">
          <button 
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-white" />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <ArrowRightIcon className="h-5 w-5 text-white" />
          </button>
        </div>
      )}

      {/* Footer with Title, Description, and Tags */}
      <div className="p-3 md:p-4 text-white shrink-0 max-h-[30vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="max-w-3xl mx-auto space-y-2 md:space-y-3">
          {imageTitle && (
            <h3 className="text-base md:text-lg font-semibold text-center">{imageTitle}</h3>
          )}
          {imageDescription && (
            <p className="text-xs md:text-sm text-white/80 text-center line-clamp-3 md:line-clamp-none">{imageDescription}</p>
          )}
          {tags && tags.length > 0 && (
            <div className="flex flex-wrap justify-center gap-1.5 md:gap-2 pt-1 md:pt-2">
              {tags.map((tag: string) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2 md:px-3 py-0.5 md:py-1 text-xs bg-white/10 text-white/90 rounded-full"
                >
                  <TagIcon className="h-2.5 w-2.5 md:h-3 md:w-3" />
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  useHideBottomNav();
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);


  // Get all images including cover image for lightbox
  const allImages = project ? [
    { url: project.data.coverImage, title: project.data.title },
    ...(project.data.images || [])
  ] : [];

  const openLightbox = useCallback((index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const goToPrev = useCallback(() => {
    setLightboxIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  }, [allImages.length]);

  const goToNext = useCallback(() => {
    setLightboxIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  }, [allImages.length]);

  const { likes, isLiked, isBookmarked, toggleLike, toggleBookmark } = useProjectInteractions(
    project?.id,
    project?.data?.category
  );

  // Track and get views - use actual project ID, not URL param (which could be slug)
  const { views } = useProjectViews(project?.id);

  // Sibling projects in the same category — for the footer prev/next nav.
  // Runs only once the current project is known; skipped for locked
  // projects since a free user shouldn't be shepherded into more of them.
  const { projects: siblings } = useProjects({
    category: project?.data?.category,
    status: 'published',
    limit: 40,
  });
  const siblingList = siblings.filter((s) => !s.data.isLocked);
  const currentIdx = project ? siblingList.findIndex((s) => s.id === project.id || s.slug === project.slug) : -1;
  const prevProject = currentIdx > 0 ? siblingList[currentIdx - 1] : null;
  const nextProject = currentIdx >= 0 && currentIdx < siblingList.length - 1 ? siblingList[currentIdx + 1] : null;

  // Save-as-PDF export. Available on every project view — the user's
  // own and anyone else's — because architects screenshot other people's
  // work into decks all the time.
  //
  // Rather than a heavy client-side PDF library, this triggers
  // window.print() with a `data-printing` marker on <html> so the
  // print stylesheet below (index.css) hides all app chrome and
  // paginates just the project's hero, meta, and gallery. Users pick
  // "Save as PDF" as the destination in the browser print dialog.
  //
  // Tier resolution uses resolveUserTier so paid users whose plan
  // lives on user.tier / metadata.tier / subscription_status are all
  // recognized — fixed alongside the earlier PDF-lock bug.
  // Export is owner-only. Non-owners can't print or screenshot a
  // project through Planbase — architects' work isn't a mood board
  // for anyone else to export. The button only renders when
  // isOwner is true, below.
  const projectOwnerId = project?.created_by || project?.data?.created_by;
  const isOwner = !!user?.id && !!projectOwnerId && user.id === projectOwnerId;

  const handlePrint = () => {
    if (!isOwner) return;
    navigate(`/project/${id}/export`);
  };

  // Fetch project from CMS
  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${PLANBASE_CONFIG.apiUrl}/sdk/cms/records/${id}`,
          {
            headers: {
              'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
              'X-Project-ID': PLANBASE_CONFIG.projectId,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setProject(data.record);
        } else {
          setError('Project not found');
        }
      } catch (err) {
        console.error('[ProjectDetail] Error:', err);
        setError('Failed to load project');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProject();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="relative w-full h-[40vh] md:h-[55vh] bg-muted animate-pulse rounded-2xl">
          <div className="absolute top-4 left-4 h-9 w-24 bg-muted-foreground/20 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          <div className="md:col-span-2 space-y-4">
            <div className="h-8 bg-muted rounded animate-pulse" />
            <div className="h-4 bg-muted rounded animate-pulse w-2/3" />
            <div className="space-y-2">
              <div className="h-4 bg-muted rounded animate-pulse" />
              <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-card border border-border rounded-xl p-6 space-y-3">
              <div className="h-5 w-32 bg-muted rounded animate-pulse" />
              {[1, 2].map((i) => (
                <div key={i} className="flex justify-between">
                  <div className="h-4 w-20 bg-muted rounded animate-pulse" />
                  <div className="h-4 w-12 bg-muted rounded animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="space-y-4">
        <div className="w-full h-[40vh] md:h-[55vh] bg-muted rounded-2xl flex flex-col items-center justify-center gap-4">
          <PhotoIcon className="h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{error || 'Project not found'}</p>
          <Button onClick={() => navigate('/discover')} variant="outline" size="sm">
            <ChevronLeftIcon className="mr-1.5 h-3.5 w-3.5" />
            Back to Discover
          </Button>
        </div>
      </div>
    );
  }



  return (
    <div className="space-y-4" data-print="show">
      {/* Cover Image */}
      <div
        className="relative w-full h-[40vh] md:h-[55vh] overflow-hidden bg-muted cursor-pointer rounded-2xl"
        onClick={() => openLightbox(0)}
      >
        {/* Back Button - Overlay on Image */}
        <div className="absolute top-3 left-3 md:top-4 md:left-4 z-10" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-white/30 bg-black/30 backdrop-blur-sm text-white text-sm active:scale-95 transition-transform"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Back
          </button>
        </div>
        {/* Cover Image Only */}
        <img
          src={project.data.coverImage}
          alt={project.data.title}
          className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-500"
          onError={(e) => {
            e.currentTarget.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop';
          }}
        />
        {/* Click to expand hint */}
        <div className="absolute bottom-3 right-3 md:bottom-4 md:right-4 px-2 py-1 md:px-3 md:py-1.5 bg-black/50 backdrop-blur-sm rounded-full text-white text-xs md:text-sm">
          Tap to expand
        </div>
      </div>

      {/* Project Info */}
      <div className="space-y-4 md:space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {/* Main Content */}
        <div className="md:col-span-2 space-y-4 md:space-y-6">
          <div>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
              <div className="flex-1 min-w-0">
                  <h1 className="text-lg md:text-xl font-bold text-foreground mb-2 break-words">{project.data.title}</h1>
                <div className="flex flex-wrap items-center gap-2 md:gap-4 text-sm md:text-base text-muted-foreground">
                  {project.data.architect && (
                    <span className="flex items-center gap-1">
                      <UserIcon className="h-3 w-3 md:h-4 md:w-4 shrink-0" />
                      <span className="truncate">{project.data.architect}</span>
                    </span>
                  )}
                  {project.data.location && (
                    <span className="flex items-center gap-1">
                      <MapPinIcon className="h-3 w-3 md:h-4 md:w-4 shrink-0" />
                      <span className="truncate">{project.data.location}</span>
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2 shrink-0" data-print="hide">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={toggleLike}
                  className={`h-9 w-9 md:h-10 md:w-10 ${isLiked ? 'text-red-500 border-red-500' : ''}`}
                >
                  <HeartIcon className={`h-4 w-4 md:h-5 md:w-5 ${isLiked ? 'fill-current' : ''}`} />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => toggleBookmark(project.data.category)}
                  className={`h-9 w-9 md:h-10 md:w-10 ${isBookmarked ? 'text-primary border-primary' : ''}`}
                >
                  <BookmarkIcon className={`h-4 w-4 md:h-5 md:w-5 ${isBookmarked ? 'fill-current' : ''}`} />
                </Button>
                <Button variant="outline" size="icon" className="h-9 w-9 md:h-10 md:w-10">
                  <ShareIcon className="h-4 w-4 md:h-5 md:w-5" />
                </Button>
                {isOwner && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handlePrint}
                    aria-label="Export to PDF"
                    title="Open PDF editor"
                    className="h-9 w-9 md:h-10 md:w-10"
                  >
                    <PrinterIcon className="h-4 w-4 md:h-5 md:w-5" />
                  </Button>
                )}
              </div>
            </div>

            <div className="text-sm md:text-base text-muted-foreground leading-relaxed whitespace-pre-wrap">
              {project.data.description}
            </div>
          </div>

          {/* Additional Images Gallery - Consistent Height Grid */}
          {project.data.images && project.data.images.length > 0 && (
            <div className="space-y-4 md:space-y-6">
              <h3 className="text-lg md:text-xl font-semibold text-foreground">Project Gallery</h3>
              <div className="grid grid-cols-2 md:grid-cols-2 gap-3 md:gap-4">
                {project.data.images.map((image: any, index: number) => (
                  <div 
                    key={index} 
                    className="space-y-1.5 md:space-y-2 cursor-pointer group"
                    onClick={() => openLightbox(index + 1)}
                  >
                    <div className="aspect-[4/3] rounded-xl overflow-hidden bg-muted">
                      <img
                        src={typeof image === 'string' ? image : image.url}
                        alt={typeof image === 'object' && image.title ? image.title : `Gallery image ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.currentTarget.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop';
                        }}
                      />
                    </div>
                    {typeof image === 'object' && (image.title || image.description) && (
                      <div className="space-y-0.5 md:space-y-1">
                        {image.title && (
                          <h4 className="text-xs md:text-sm font-medium text-foreground group-hover:text-primary transition-colors line-clamp-1">{image.title}</h4>
                        )}
                        {image.description && (
                          <p className="text-xs text-muted-foreground line-clamp-1 md:line-clamp-2 hidden sm:block">{image.description}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {project.data.tags && project.data.tags.length > 0 && (
            <div className="space-y-2 md:space-y-3">
              <h3 className="text-base md:text-lg font-semibold text-foreground flex items-center gap-2">
                <TagIcon className="h-3.5 w-3.5 md:h-4 md:w-4" />
                Tags
              </h3>
              <div className="flex flex-wrap gap-1.5 md:gap-2">
                {project.data.tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 md:px-3 py-1 md:py-1.5 text-xs md:text-sm bg-secondary text-secondary-foreground hover:bg-muted rounded-full transition-colors cursor-pointer"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div>
          <div className="bg-card border border-border rounded-2xl overflow-hidden">

            {/* Likes */}
            <div className="flex items-center justify-between px-4 py-3.5">
              <span className="flex items-center gap-3 text-sm text-foreground">
                <HeartIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                Likes
              </span>
              <span className="text-sm font-medium text-foreground">{likes}</span>
            </div>

            {/* Views */}
            <div className="border-t border-border flex items-center justify-between px-4 py-3.5">
              <span className="flex items-center gap-3 text-sm text-foreground">
                <EyeIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                Views
              </span>
              <span className="text-sm font-medium text-foreground">{views}</span>
            </div>

            {/* Category */}
            {project.data.category && (
              <div className="border-t border-border flex items-center justify-between px-4 py-3.5">
                <span className="flex items-center gap-3 text-sm text-foreground">
                  <BuildingOffice2Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  Category
                </span>
                <span className="text-sm text-muted-foreground capitalize">{project.data.category}</span>
              </div>
            )}

            {/* Style */}
            {project.data.style && (
              <div className="border-t border-border flex items-center justify-between px-4 py-3.5">
                <span className="flex items-center gap-3 text-sm text-foreground">
                  <SwatchIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  Style
                </span>
                <span className="text-sm text-muted-foreground capitalize">{project.data.style}</span>
              </div>
            )}

            {/* Year */}
            {project.data.year && (
              <div className="border-t border-border flex items-center justify-between px-4 py-3.5">
                <span className="flex items-center gap-3 text-sm text-foreground">
                  <CalendarIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  Year
                </span>
                <span className="text-sm text-muted-foreground">{project.data.year}</span>
              </div>
            )}

            {/* Location */}
            {project.data.location && (
              <div className="border-t border-border flex items-center justify-between px-4 py-3.5">
                <span className="flex items-center gap-3 text-sm text-foreground">
                  <MapPinIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  Location
                </span>
                <span className="text-sm text-muted-foreground text-right max-w-[55%] truncate">{project.data.location}</span>
              </div>
            )}

            {/* Architect — links through to the owner's public portfolio
                when we know their username, so readers can jump to the
                rest of their work. Falls back to a static card for
                records with no linked portfolio (older imports, etc.). */}
            {project.data.architect && (
              project.data.owner_username ? (
                <button
                  type="button"
                  onClick={() => navigate(`/portfolio/${project.data.owner_username}`)}
                  className="border-t border-border flex items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-muted/50 w-full"
                  aria-label={`View ${project.data.owner_full_name || project.data.architect}'s portfolio`}
                >
                  <UserAvatar name={project.data.owner_full_name || project.data.architect} size="sm" className="flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-muted-foreground">Architect</p>
                    <p className="text-sm text-foreground truncate group-hover:text-primary">
                      {project.data.owner_full_name || project.data.architect}
                    </p>
                  </div>
                  <ArrowRightIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                </button>
              ) : (
                <div className="border-t border-border flex items-center gap-3 px-4 py-3.5">
                  <UserAvatar name={project.data.architect} size="sm" className="flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Architect</p>
                    <p className="text-sm text-foreground truncate">{project.data.architect}</p>
                  </div>
                </div>
              )
            )}

          </div>
        </div>
      </div>
    </div>

      {/*
        Footer nav — keeps readers inside the feed. Two cards
        (previous / next project in the same category); on narrow
        screens they stack. Silent no-op if this is the only project
        in the category.
      */}
      {(prevProject || nextProject) && (
        <div className="mt-10 grid grid-cols-1 gap-3 border-t border-border pt-6 md:grid-cols-2 md:gap-4">
          {prevProject ? (
            <button
              type="button"
              onClick={() => navigate(`/project/${prevProject.slug || prevProject.id}`)}
              className="group flex items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left transition-colors hover:border-primary/60"
            >
              <img
                src={prevProject.data.coverImage}
                alt=""
                className="h-16 w-24 flex-shrink-0 rounded-lg object-cover"
                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  <ChevronLeftIcon className="h-3 w-3" />
                  Previous
                </div>
                <div className="mt-0.5 truncate text-sm font-medium text-foreground group-hover:text-primary">
                  {prevProject.data.title}
                </div>
                {prevProject.data.architect && (
                  <div className="truncate text-xs text-muted-foreground">{prevProject.data.architect}</div>
                )}
              </div>
            </button>
          ) : <div className="hidden md:block" />}
          {nextProject ? (
            <button
              type="button"
              onClick={() => navigate(`/project/${nextProject.slug || nextProject.id}`)}
              className="group flex items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left transition-colors hover:border-primary/60 md:justify-end"
            >
              <div className="min-w-0 flex-1 md:text-right">
                <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground md:justify-end">
                  Next
                  <ArrowRightIcon className="h-3 w-3" />
                </div>
                <div className="mt-0.5 truncate text-sm font-medium text-foreground group-hover:text-primary">
                  {nextProject.data.title}
                </div>
                {nextProject.data.architect && (
                  <div className="truncate text-xs text-muted-foreground">{nextProject.data.architect}</div>
                )}
              </div>
              <img
                src={nextProject.data.coverImage}
                alt=""
                className="h-16 w-24 flex-shrink-0 rounded-lg object-cover"
                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
              />
            </button>
          ) : <div className="hidden md:block" />}
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={allImages}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrev={goToPrev}
          onNext={goToNext}
          tags={project.data.tags}
        />
      )}
    </div>
  );
}
